import type { DietPlan, DietFoodItem } from '../services/scanService';
import type { AcneSeverity } from '../services/scanService';

import type { RegionAcneSummary } from '../services/scanService';

/**
 * Generates a personalized diet plan based on scan results
 * @param subscriptionTier - Determines the level of food scoring sophistication
 * @param skinConditionFocus - Determines which skin conditions to focus on:
 *   - "acne-oily": Only acne and oily skin diet recommendations (Plus)
 *   - "expanded": Acne, oily, dryness, sensitivity diet recommendations (Pro)
 *   - "comprehensive": All conditions including eczema, rosacea, hyperpigmentation, aging (Elite)
 */
export function generateDietPlan(
  glowScore: number,
  acneSeverity: AcneSeverity,
  estimatedPH?: number,
  dietaryPreference?: 'vegetarian' | 'non-vegetarian',
  subscriptionTier?: 'free' | 'plus' | 'pro' | 'elite',
  skinConditionFocus: 'acne-oily' | 'expanded' | 'comprehensive' = 'acne-oily',
  regions?: RegionAcneSummary[],
  rekognitionMetadata?: {
    age?: number;
    gender?: 'Male' | 'Female';
    stressLevel?: 'low' | 'medium' | 'high';
  }
): DietPlan {
  // Users with acne: 1 gallon per day. Others: 8-10 glasses
  const hasAcne = acneSeverity === 'mild_to_moderate' || acneSeverity === 'moderate' ||
    acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe';

  let waterIntakeGlasses: number;
  let waterIntakeDisplay: string | undefined;
  let waterIntakeSkinNote: string | undefined;

  if (hasAcne) {
    waterIntakeGlasses = 16; // 1 gallon = 16 x 8oz glasses
    waterIntakeDisplay = '1 gallon';
    waterIntakeSkinNote = 'Helps flush toxins, reduce inflammation, plump skin cells, and support your skin\'s natural healing—essential for clearer, healthier-looking skin.';
  } else {
    const baseWaterIntake = glowScore < 50 ? 10 : glowScore < 70 ? 9 : 8;
    if (rekognitionMetadata?.age) {
      if (rekognitionMetadata.age < 18) {
        waterIntakeGlasses = Math.max(8, baseWaterIntake - 1);
      } else if (rekognitionMetadata.age > 50) {
        waterIntakeGlasses = baseWaterIntake + 1;
      } else {
        waterIntakeGlasses = baseWaterIntake;
      }
    } else {
      waterIntakeGlasses = baseWaterIntake;
    }
    if (rekognitionMetadata?.stressLevel === 'high') {
      waterIntakeGlasses += 1;
    }
  }

  // Foods to eat - based on skin health and acne severity
  const foodsToEat: DietFoodItem[] = [];
  
  // Extract comprehensive data from regions if available
  const regionsData = regions || [];
  const totalActiveLesions = regionsData.reduce((sum, r) => sum + r.activeLesionCount, 0);
  const totalComedones = regionsData.reduce((sum, r) => sum + r.comedoneCount, 0);
  const hyperpigmentationRegions = regionsData.filter(r => r.hyperpigmentationLevel !== 'none');
  const textureDescriptions = regionsData.map(r => r.textureDescription.toLowerCase()).join(' ');
  const hasRoughTexture = textureDescriptions.includes('rough') || textureDescriptions.includes('uneven');
  const hasDryTexture = textureDescriptions.includes('dry') || textureDescriptions.includes('dehydrated');
  const hasOilyTexture = textureDescriptions.includes('oily') || textureDescriptions.includes('shiny');
  
  // Use a comprehensive deterministic seed based on ALL scan parameters to vary food selection
  // This ensures different scans get different food recommendations based on their unique skin profile
  const foodSeed = Math.floor(
    (glowScore * 50) +
    ((estimatedPH || 5.0) * 20) +
    (acneSeverity === 'severe' ? 200 : acneSeverity === 'moderate' ? 100 : 0) +
    (totalActiveLesions * 10) +
    (totalComedones * 5) +
    (hyperpigmentationRegions.length * 30) +
    (regionsData.length * 15) +
    (hasRoughTexture ? 150 : 0) +
    (hasDryTexture ? 100 : 0) +
    (hasOilyTexture ? 125 : 0)
  ) % 5000;
  
  // Simple seeded shuffle function for foods
  const seededShuffleFoods = <T,>(array: T[], seed: number): T[] => {
    const shuffled = [...array];
    let currentSeed = seed;
    for (let i = shuffled.length - 1; i > 0; i--) {
      currentSeed = (currentSeed * 9301 + 49297) % 233280;
      const j = Math.floor((currentSeed / 233280) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Full meals for non-vegetarians (also include when preference is not set)
  if (dietaryPreference !== 'vegetarian') {
    // Full Meal 1: Breakfast
    foodsToEat.push({
      type: 'meal',
      name: 'Grilled Salmon with Quinoa and Roasted Vegetables',
      description: 'Complete meal rich in omega-3 fatty acids, complete protein, and antioxidants. Reduces inflammation and supports skin barrier health.',
      recipe: {
        title: 'Grilled Salmon with Quinoa and Roasted Vegetables',
        ingredients: [
          '1 salmon fillet (6 oz)',
          '1/2 cup cooked quinoa',
          '1 cup mixed vegetables (broccoli, bell peppers, zucchini, carrots)',
          '1 tbsp olive oil',
          '1 lemon',
          '2 cloves garlic, minced',
          'Fresh herbs (dill or parsley)',
          'Salt and pepper to taste'
        ],
        steps: [
          'Preheat oven to 400°F (200°C).',
          'Toss vegetables with olive oil, garlic, salt, and pepper. Roast for 20 minutes.',
          'Season salmon with salt, pepper, and lemon juice.',
          'Grill or pan-sear salmon for 4-5 minutes per side until flaky.',
          'Cook quinoa according to package directions.',
          'Serve salmon over quinoa with roasted vegetables. Garnish with fresh herbs.'
        ],
        servings: 1,
        caloriesPerServing: 480
      }
    });

    // Full Meal 2: Lunch
    foodsToEat.push({
      type: 'meal',
      name: 'Grilled Chicken and Vegetable Stir-Fry with Brown Rice',
      description: 'Lean protein with fiber-rich vegetables and whole grains. Supports skin repair and stabilizes blood sugar.',
      recipe: {
        title: 'Grilled Chicken and Vegetable Stir-Fry with Brown Rice',
        ingredients: [
          '1 chicken breast (6 oz), sliced',
          '1/2 cup cooked brown rice',
          '1 cup mixed vegetables (bell peppers, broccoli, snap peas, carrots)',
          '1 tbsp olive oil',
          '2 cloves garlic, minced',
          '1 tsp ginger, grated',
          '2 tbsp low-sodium soy sauce or tamari',
          '1 tsp sesame oil (optional)',
          'Green onions for garnish'
        ],
        steps: [
          'Cook brown rice according to package directions.',
          'Heat olive oil in a large pan over medium-high heat.',
          'Add chicken and cook until golden, about 5-6 minutes.',
          'Add garlic and ginger, stir for 30 seconds.',
          'Add vegetables and stir-fry for 4-5 minutes until crisp-tender.',
          'Add soy sauce and sesame oil, toss to combine.',
          'Serve over brown rice, garnish with green onions.'
        ],
        servings: 1,
        caloriesPerServing: 450
      }
    });

    // Full Meal 3: Dinner
    foodsToEat.push({
      type: 'meal',
      name: 'Baked Turkey Meatballs with Zucchini Noodles',
      description: 'Lean protein with vegetable noodles. High in protein, low in carbs, anti-inflammatory.',
      recipe: {
        title: 'Baked Turkey Meatballs with Zucchini Noodles',
        ingredients: [
          '4 oz ground turkey',
          '2 medium zucchinis, spiralized',
          '1/4 cup whole wheat breadcrumbs',
          '1 egg',
          '1 clove garlic, minced',
          '1 tbsp olive oil',
          '1/2 cup marinara sauce (low sugar)',
          'Fresh basil',
          'Salt and pepper to taste'
        ],
        steps: [
          'Preheat oven to 375°F (190°C).',
          'Mix turkey, breadcrumbs, egg, garlic, salt, and pepper. Form into meatballs.',
          'Bake meatballs for 20-25 minutes until cooked through.',
          'Heat olive oil in a pan, sauté zucchini noodles for 2-3 minutes.',
          'Warm marinara sauce, add meatballs.',
          'Serve meatballs and sauce over zucchini noodles, garnish with basil.'
        ],
        servings: 1,
        caloriesPerServing: 380
      }
    });

    // Full Meal 4: Breakfast
    foodsToEat.push({
      type: 'meal',
      name: 'Scrambled Eggs with Spinach and Whole Grain Toast',
      description: 'High-protein breakfast with leafy greens and whole grains. Supports skin repair and provides essential nutrients.',
      recipe: {
        title: 'Scrambled Eggs with Spinach and Whole Grain Toast',
        ingredients: [
          '2 eggs',
          '1 cup fresh spinach',
          '1 slice whole grain bread',
          '1 tbsp olive oil',
          '1 clove garlic, minced',
          'Salt and pepper to taste'
        ],
        steps: [
          'Toast whole grain bread.',
          'Heat olive oil in a pan, sauté garlic and spinach until wilted.',
          'Scramble eggs in the same pan with spinach.',
          'Season with salt and pepper.',
          'Serve eggs with toast.'
        ],
        servings: 1,
        caloriesPerServing: 320
      }
    });

    // Full Meal 5: Lunch
    foodsToEat.push({
      type: 'meal',
      name: 'Turkey and Avocado Wrap with Vegetables',
      description: 'Lean protein with healthy fats and fiber. Supports skin health and provides sustained energy.',
      recipe: {
        title: 'Turkey and Avocado Wrap with Vegetables',
        ingredients: [
          '1 whole wheat tortilla',
          '3 oz sliced turkey breast',
          '1/4 avocado, sliced',
          '1/4 cup mixed vegetables (lettuce, tomatoes, bell peppers)',
          '1 tbsp hummus',
          'Salt and pepper to taste'
        ],
        steps: [
          'Spread hummus on tortilla.',
          'Layer turkey, avocado, and vegetables.',
          'Season with salt and pepper.',
          'Roll tightly and serve.'
        ],
        servings: 1,
        caloriesPerServing: 340
      }
    });

    // Full Meal 6: Dinner
    foodsToEat.push({
      type: 'meal',
      name: 'Baked Chicken Thighs with Roasted Vegetables and Quinoa',
      description: 'Complete meal with protein, complex carbs, and vegetables. Supports skin repair and provides balanced nutrition.',
      recipe: {
        title: 'Baked Chicken Thighs with Roasted Vegetables and Quinoa',
        ingredients: [
          '2 chicken thighs (bone-in, skin removed)',
          '1/2 cup cooked quinoa',
          '1 cup mixed vegetables (broccoli, carrots, bell peppers)',
          '1 tbsp olive oil',
          '2 cloves garlic, minced',
          'Fresh rosemary',
          'Salt and pepper to taste'
        ],
        steps: [
          'Preheat oven to 400°F (200°C).',
          'Season chicken with garlic, rosemary, salt, and pepper.',
          'Toss vegetables with olive oil, salt, and pepper.',
          'Bake chicken and vegetables for 25-30 minutes.',
          'Cook quinoa according to package directions.',
          'Serve chicken and vegetables over quinoa.'
        ],
        servings: 1,
        caloriesPerServing: 460
      }
    });

    // Snack 1
    foodsToEat.push({
      type: 'snack',
      name: 'Greek Yogurt with Berries and Almonds',
      description: 'Protein-rich snack with antioxidants. Supports gut health and provides vitamin C for collagen production.',
      recipe: {
        title: 'Greek Yogurt with Berries and Almonds',
        ingredients: [
          '1/2 cup plain Greek yogurt',
          '1/2 cup mixed berries (blueberries, strawberries)',
          '1 tbsp sliced almonds',
          '1 tsp honey (optional)'
        ],
        steps: [
          'Scoop Greek yogurt into a bowl.',
          'Top with fresh berries and almonds.',
          'Drizzle with honey if desired.',
          'Enjoy as a healthy snack or light breakfast.'
        ],
        servings: 1,
        caloriesPerServing: 180
      }
    });

    // Snack 2
    foodsToEat.push({
      type: 'snack',
      name: 'Hard-Boiled Eggs with Whole Grain Crackers',
      description: 'High-protein snack with complex carbs. Supports skin repair and provides sustained energy.',
      recipe: {
        title: 'Hard-Boiled Eggs with Whole Grain Crackers',
        ingredients: [
          '2 hard-boiled eggs',
          '6-8 whole grain crackers',
          'Salt and pepper to taste'
        ],
        steps: [
          'Boil eggs for 8-10 minutes until hard-boiled.',
          'Peel and slice eggs.',
          'Serve with whole grain crackers.',
          'Season with salt and pepper if desired.'
        ],
        servings: 1,
        caloriesPerServing: 200
      }
    });

    // Snack 3
    foodsToEat.push({
      type: 'snack',
      name: 'Apple Slices with Peanut Butter',
      description: 'Fiber-rich snack with protein and healthy fats. Supports stable blood sugar and skin health.',
      recipe: {
        title: 'Apple Slices with Peanut Butter',
        ingredients: [
          '1 medium apple, sliced',
          '2 tbsp natural peanut butter',
          '1 tsp chia seeds (optional)'
        ],
        steps: [
          'Slice apple into wedges.',
          'Serve with peanut butter for dipping.',
          'Sprinkle with chia seeds if desired.',
          'Enjoy as a satisfying snack.'
        ],
        servings: 1,
        caloriesPerServing: 290
      }
    });

    // Snack 4
    foodsToEat.push({
      type: 'snack',
      name: 'Tuna Salad with Whole Grain Crackers',
      description: 'Omega-3 rich snack with protein and complex carbs. Reduces inflammation and supports skin barrier.',
      recipe: {
        title: 'Tuna Salad with Whole Grain Crackers',
        ingredients: [
          '1 can (3 oz) tuna in water, drained',
          '1 tbsp Greek yogurt or light mayo',
          '1/4 cup diced celery',
          '1 tbsp diced red onion',
          '1 tsp lemon juice',
          '6-8 whole grain crackers',
          'Salt and pepper to taste'
        ],
        steps: [
          'Mix tuna with Greek yogurt, celery, and red onion.',
          'Add lemon juice, salt, and pepper.',
          'Serve with whole grain crackers.',
          'Enjoy as a protein-rich snack.'
        ],
        servings: 1,
        caloriesPerServing: 240
      }
    });
  }

  // Full meals for vegetarians; for non-vegetarian users, also add vegetarian meals (mix of both)
  if (dietaryPreference === 'vegetarian' || !dietaryPreference || dietaryPreference === 'non-vegetarian') {
    // Full Meal 1: Breakfast
    foodsToEat.push({
      type: 'meal',
      name: 'Quinoa Buddha Bowl with Chickpeas and Vegetables',
      description: 'Complete plant-based meal with protein, fiber, and antioxidants. Reduces inflammation and supports skin health.',
      recipe: {
        title: 'Quinoa Buddha Bowl with Chickpeas and Vegetables',
        ingredients: [
          '1/2 cup cooked quinoa',
          '1/2 cup chickpeas (canned, drained)',
          '1 cup mixed vegetables (roasted sweet potato, bell peppers, broccoli, spinach)',
          '1 tbsp olive oil',
          '1/4 avocado, sliced',
          '1 tbsp tahini',
          '1 tsp lemon juice',
          'Salt and pepper to taste'
        ],
        steps: [
          'Cook quinoa according to package directions.',
          'Roast sweet potato and vegetables with olive oil at 400°F for 20 minutes.',
          'Heat chickpeas in a pan with a pinch of salt.',
          'Mix tahini with lemon juice and a little water for dressing.',
          'Layer quinoa, vegetables, and chickpeas in a bowl.',
          'Top with avocado and drizzle with tahini dressing.'
        ],
        servings: 1,
        caloriesPerServing: 420
      }
    });

    // Full Meal 2: Lunch
    foodsToEat.push({
      type: 'meal',
      name: 'Lentil Curry with Brown Rice and Steamed Vegetables',
      description: 'High-protein vegetarian meal with anti-inflammatory spices. Rich in fiber and supports stable blood sugar.',
      recipe: {
        title: 'Lentil Curry with Brown Rice and Steamed Vegetables',
        ingredients: [
          '1/2 cup cooked brown rice',
          '1/2 cup red lentils, cooked',
          '1 cup mixed vegetables (cauliflower, carrots, peas)',
          '1/2 onion, diced',
          '2 cloves garlic, minced',
          '1 tsp turmeric',
          '1/2 tsp cumin',
          '1/2 tsp coriander',
          '1 cup vegetable broth',
          '1 tbsp olive oil',
          'Fresh cilantro'
        ],
        steps: [
          'Cook brown rice according to package directions.',
          'Heat olive oil, sauté onion and garlic until soft.',
          'Add spices, stir for 30 seconds.',
          'Add lentils and vegetable broth, simmer for 15 minutes.',
          'Steam vegetables until tender.',
          'Serve curry over brown rice with steamed vegetables, garnish with cilantro.'
        ],
        servings: 1,
        caloriesPerServing: 390
      }
    });

    // Full Meal 3: Dinner
    foodsToEat.push({
      type: 'meal',
      name: 'Chickpea and Vegetable Stir-Fry with Whole Wheat Noodles',
      description: 'Plant-based protein with whole grains and vegetables. High in fiber, supports skin barrier health.',
      recipe: {
        title: 'Chickpea and Vegetable Stir-Fry with Whole Wheat Noodles',
        ingredients: [
          '2 oz whole wheat noodles',
          '1/2 cup chickpeas (canned, drained)',
          '1 cup mixed vegetables (bell peppers, broccoli, snap peas, carrots)',
          '1 tbsp olive oil',
          '2 cloves garlic, minced',
          '1 tsp ginger, grated',
          '2 tbsp low-sodium soy sauce or tamari',
          '1 tsp sesame oil',
          'Green onions for garnish'
        ],
        steps: [
          'Cook whole wheat noodles according to package directions.',
          'Heat olive oil in a large pan over medium-high heat.',
          'Add chickpeas and cook until slightly crispy, about 5 minutes.',
          'Add garlic and ginger, stir for 30 seconds.',
          'Add vegetables and stir-fry for 4-5 minutes until crisp-tender.',
          'Add cooked noodles, soy sauce, and sesame oil, toss to combine.',
          'Garnish with green onions and serve.'
        ],
        servings: 1,
        caloriesPerServing: 410
      }
    });

    // Full Meal 4: Breakfast
    foodsToEat.push({
      type: 'meal',
      name: 'Overnight Oats with Berries and Almonds',
      description: 'High-fiber breakfast with antioxidants and healthy fats. Supports stable blood sugar and skin health.',
      recipe: {
        title: 'Overnight Oats with Berries and Almonds',
        ingredients: [
          '1/2 cup rolled oats',
          '1/2 cup almond milk',
          '1 tbsp chia seeds',
          '1/2 cup mixed berries',
          '1 tbsp sliced almonds',
          '1 tsp honey or maple syrup',
          '1/4 tsp cinnamon'
        ],
        steps: [
          'Mix oats, almond milk, chia seeds, and cinnamon in a jar.',
          'Stir in sweetener.',
          'Refrigerate overnight or at least 4 hours.',
          'Top with fresh berries and almonds before serving.'
        ],
        servings: 1,
        caloriesPerServing: 290
      }
    });

    // Full Meal 5: Lunch
    foodsToEat.push({
      type: 'meal',
      name: 'Mediterranean Chickpea Salad Wrap',
      description: 'Protein-rich wrap with vegetables and healthy fats. Anti-inflammatory and supports skin barrier.',
      recipe: {
        title: 'Mediterranean Chickpea Salad Wrap',
        ingredients: [
          '1 whole wheat tortilla or wrap',
          '1/2 cup chickpeas (canned, drained)',
          '1/4 cup diced cucumber',
          '1/4 cup diced tomatoes',
          '2 tbsp red onion, diced',
          '1 tbsp olive oil',
          '1 tsp lemon juice',
          'Fresh parsley',
          'Salt and pepper to taste'
        ],
        steps: [
          'Mash half the chickpeas with a fork.',
          'Mix with whole chickpeas, cucumber, tomatoes, and red onion.',
          'Dress with olive oil, lemon juice, parsley, salt, and pepper.',
          'Spoon mixture onto tortilla and wrap tightly.',
          'Serve immediately or refrigerate for later.'
        ],
        servings: 1,
        caloriesPerServing: 350
      }
    });

    // Full Meal 6: Dinner
    foodsToEat.push({
      type: 'meal',
      name: 'Stuffed Bell Peppers with Quinoa and Black Beans',
      description: 'Complete plant-based meal with protein, fiber, and vitamins. Supports skin health and stable blood sugar.',
      recipe: {
        title: 'Stuffed Bell Peppers with Quinoa and Black Beans',
        ingredients: [
          '2 bell peppers, halved and seeded',
          '1/2 cup cooked quinoa',
          '1/2 cup black beans (canned, drained)',
          '1/4 cup corn',
          '1/4 cup diced tomatoes',
          '2 tbsp olive oil',
          '1 clove garlic, minced',
          '1/4 tsp cumin',
          'Fresh cilantro',
          'Salt and pepper to taste'
        ],
        steps: [
          'Preheat oven to 375°F (190°C).',
          'Cook quinoa according to package directions.',
          'Mix quinoa, black beans, corn, tomatoes, garlic, cumin, salt, and pepper.',
          'Stuff bell pepper halves with mixture.',
          'Drizzle with olive oil, bake for 25-30 minutes until peppers are tender.',
          'Garnish with cilantro and serve.'
        ],
        servings: 1,
        caloriesPerServing: 380
      }
    });

    // Snack 1
    foodsToEat.push({
      type: 'snack',
      name: 'Chia Seed Pudding with Berries and Nuts',
      description: 'Plant-based omega-3 source with antioxidants. Reduces inflammation and supports skin barrier health.',
      recipe: {
        title: 'Chia Seed Pudding with Berries and Nuts',
        ingredients: [
          '3 tbsp chia seeds',
          '1 cup almond milk or coconut milk',
          '1/2 cup mixed berries',
          '1 tbsp sliced almonds',
          '1 tbsp walnuts',
          '1 tsp honey or maple syrup (optional)'
        ],
        steps: [
          'Mix chia seeds with almond milk and sweetener.',
          'Stir well and let sit for 10 minutes, then stir again.',
          'Refrigerate for at least 2 hours or overnight.',
          'Top with fresh berries, almonds, and walnuts before serving.'
        ],
        servings: 1,
        caloriesPerServing: 320
      }
    });

    // Snack 2
    foodsToEat.push({
      type: 'snack',
      name: 'Hummus with Fresh Vegetables',
      description: 'Protein-rich snack with fiber and healthy fats. Supports gut health and skin barrier.',
      recipe: {
        title: 'Hummus with Fresh Vegetables',
        ingredients: [
          '1/2 cup hummus (store-bought or homemade)',
          '1 cup fresh vegetables (carrot sticks, cucumber slices, bell pepper strips)',
          '1 tbsp olive oil (for homemade hummus)',
          '1 tsp lemon juice'
        ],
        steps: [
          'If making homemade: blend chickpeas, tahini, olive oil, lemon juice, and garlic.',
          'Serve hummus in a bowl.',
          'Arrange fresh vegetables around the hummus.',
          'Enjoy as a healthy snack or light meal.'
        ],
        servings: 1,
        caloriesPerServing: 220
      }
    });

    // Snack 3
    foodsToEat.push({
      type: 'snack',
      name: 'Apple Slices with Almond Butter',
      description: 'Fiber-rich snack with healthy fats and antioxidants. Supports stable blood sugar and skin health.',
      recipe: {
        title: 'Apple Slices with Almond Butter',
        ingredients: [
          '1 medium apple, sliced',
          '2 tbsp almond butter',
          '1 tsp chia seeds (optional)'
        ],
        steps: [
          'Slice apple into wedges.',
          'Serve with almond butter for dipping.',
          'Sprinkle with chia seeds if desired.',
          'Enjoy as a satisfying snack.'
        ],
        servings: 1,
        caloriesPerServing: 280
      }
    });
  }

  // Add more full meals based on severity
  if (acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    if (dietaryPreference !== 'vegetarian') {
      // Additional meal 1 for non-vegetarians with moderate/severe acne
      foodsToEat.push({
        type: 'meal',
        name: 'Baked Cod with Sweet Potato and Steamed Broccoli',
        description: 'Lean white fish with complex carbs and cruciferous vegetables. Anti-inflammatory and supports skin healing.',
        recipe: {
          title: 'Baked Cod with Sweet Potato and Steamed Broccoli',
          ingredients: [
            '1 cod fillet (6 oz)',
            '1 medium sweet potato, baked',
            '1 cup broccoli, steamed',
            '1 tbsp olive oil',
            '1 lemon',
            'Fresh dill',
            'Salt and pepper to taste'
          ],
          steps: [
            'Preheat oven to 400°F (200°C).',
            'Bake sweet potato for 45 minutes until tender.',
            'Season cod with salt, pepper, and lemon juice.',
            'Bake cod for 12-15 minutes until flaky.',
            'Steam broccoli until bright green and tender.',
            'Serve cod with sweet potato and broccoli, garnish with dill.'
          ],
          servings: 1,
          caloriesPerServing: 360
        }
      });

      // Additional meal 2
      foodsToEat.push({
        type: 'meal',
        name: 'Lean Beef and Vegetable Skewers with Quinoa',
        description: 'High-protein meal with iron and zinc. Supports skin repair and provides essential minerals.',
        recipe: {
          title: 'Lean Beef and Vegetable Skewers with Quinoa',
          ingredients: [
            '4 oz lean beef, cubed',
            '1/2 cup cooked quinoa',
            '1 cup mixed vegetables (bell peppers, zucchini, mushrooms)',
            '1 tbsp olive oil',
            '2 cloves garlic, minced',
            '1 tsp rosemary',
            'Salt and pepper to taste'
          ],
          steps: [
            'Marinate beef with olive oil, garlic, rosemary, salt, and pepper for 30 minutes.',
            'Thread beef and vegetables onto skewers.',
            'Grill or broil for 8-10 minutes, turning occasionally.',
            'Cook quinoa according to package directions.',
            'Serve skewers over quinoa.'
          ],
          servings: 1,
          caloriesPerServing: 420
        }
      });
    } else {
      // Additional meal 1 for vegetarians with moderate/severe acne
      foodsToEat.push({
        type: 'meal',
        name: 'Tofu and Vegetable Curry with Brown Rice',
        description: 'Plant-based protein with anti-inflammatory spices. High in fiber and supports stable blood sugar.',
        recipe: {
          title: 'Tofu and Vegetable Curry with Brown Rice',
          ingredients: [
            '1/2 cup cooked brown rice',
            '4 oz firm tofu, cubed',
            '1 cup mixed vegetables (cauliflower, bell peppers, green beans)',
            '1/2 onion, diced',
            '2 cloves garlic, minced',
            '1 tsp turmeric',
            '1/2 tsp cumin',
            '1/2 tsp coriander',
            '1 cup coconut milk (light)',
            '1 tbsp olive oil',
            'Fresh cilantro'
          ],
          steps: [
            'Cook brown rice according to package directions.',
            'Heat olive oil, sauté onion and garlic until soft.',
            'Add spices, stir for 30 seconds.',
            'Add tofu and vegetables, cook for 5 minutes.',
            'Add coconut milk, simmer for 10 minutes.',
            'Serve over brown rice, garnish with cilantro.'
          ],
          servings: 1,
          caloriesPerServing: 440
        }
      });

      // Additional meal 2
      foodsToEat.push({
        type: 'meal',
        name: 'Black Bean and Sweet Potato Bowl',
        description: 'High-fiber meal with plant protein and antioxidants. Supports stable blood sugar and skin health.',
        recipe: {
          title: 'Black Bean and Sweet Potato Bowl',
          ingredients: [
            '1 medium sweet potato, roasted and cubed',
            '1/2 cup black beans (canned, drained)',
            '1/2 cup cooked quinoa',
            '1/4 avocado, sliced',
            '1/4 cup corn',
            '1 tbsp olive oil',
            '1 tsp lime juice',
            'Fresh cilantro',
            'Salt and pepper to taste'
          ],
          steps: [
            'Roast sweet potato at 400°F for 30 minutes, then cube.',
            'Cook quinoa according to package directions.',
            'Heat black beans and corn.',
            'Layer quinoa, sweet potato, black beans, and corn in a bowl.',
            'Top with avocado, drizzle with olive oil and lime juice.',
            'Garnish with cilantro and serve.'
          ],
          servings: 1,
          caloriesPerServing: 400
        }
      });
    }

    // Anti-inflammatory drink for all users
    foodsToEat.push({
      type: 'snack',
      name: 'Turmeric Golden Milk',
      description: 'Anti-inflammatory drink that helps reduce acne inflammation and redness. Best consumed in the evening.',
      recipe: {
        title: 'Turmeric Golden Milk',
        ingredients: [
          '1 cup almond milk or coconut milk',
          '1 tsp turmeric powder',
          '1/2 tsp ginger (fresh or ground)',
          '1/4 tsp cinnamon',
          '1 tsp honey (optional)',
          'Pinch of black pepper'
        ],
        steps: [
          'Heat almond milk in a small saucepan over medium heat.',
          'Whisk in turmeric, ginger, cinnamon, and black pepper.',
          'Simmer for 5 minutes, stirring occasionally.',
          'Remove from heat and stir in honey if using.',
          'Strain if needed and serve warm.'
        ],
        servings: 1,
        caloriesPerServing: 80
      }
    });
  }

  // Add more meals and snacks for all users regardless of score
  // Additional breakfast option
  if (dietaryPreference !== 'vegetarian') {
    foodsToEat.push({
      type: 'meal',
      name: 'Oatmeal with Berries and Walnuts',
      description: 'High-fiber breakfast with antioxidants and omega-3. Supports stable blood sugar and skin health.',
      recipe: {
        title: 'Oatmeal with Berries and Walnuts',
        ingredients: [
          '1/2 cup rolled oats',
          '1 cup water or almond milk',
          '1/2 cup mixed berries',
          '1 tbsp chopped walnuts',
          '1 tsp honey or maple syrup',
          '1/4 tsp cinnamon'
        ],
        steps: [
          'Cook oats with water or milk according to package directions.',
          'Stir in cinnamon and sweetener.',
          'Top with fresh berries and walnuts.',
          'Serve warm.'
        ],
        servings: 1,
        caloriesPerServing: 280
      }
    });
  } else {
    foodsToEat.push({
      type: 'meal',
      name: 'Avocado Toast with Whole Grain Bread',
      description: 'Healthy fats and fiber-rich breakfast. Supports skin barrier health and provides sustained energy.',
      recipe: {
        title: 'Avocado Toast with Whole Grain Bread',
        ingredients: [
          '2 slices whole grain bread',
          '1/2 avocado, mashed',
          '1/4 cup cherry tomatoes, halved',
          '1 tbsp pumpkin seeds',
          '1 tsp lemon juice',
          'Salt and pepper to taste'
        ],
        steps: [
          'Toast whole grain bread.',
          'Mash avocado with lemon juice, salt, and pepper.',
          'Spread avocado on toast.',
          'Top with cherry tomatoes and pumpkin seeds.',
          'Serve immediately.'
        ],
        servings: 1,
        caloriesPerServing: 320
      }
    });
  }

  // Additional lunch option
  if (dietaryPreference !== 'vegetarian') {
    foodsToEat.push({
      type: 'meal',
      name: 'Grilled Shrimp Salad with Mixed Greens',
      description: 'Lean protein with leafy greens and vegetables. High in antioxidants and supports skin health.',
      recipe: {
        title: 'Grilled Shrimp Salad with Mixed Greens',
        ingredients: [
          '4 oz shrimp, peeled and deveined',
          '2 cups mixed greens (spinach, arugula, romaine)',
          '1/4 cup cherry tomatoes',
          '1/4 cup cucumber, sliced',
          '1/4 avocado, sliced',
          '1 tbsp olive oil',
          '1 tsp lemon juice',
          'Salt and pepper to taste'
        ],
        steps: [
          'Season shrimp with salt, pepper, and lemon juice.',
          'Grill or pan-sear shrimp for 2-3 minutes per side.',
          'Toss greens with tomatoes, cucumber, and avocado.',
          'Top salad with grilled shrimp.',
          'Drizzle with olive oil and lemon juice, serve.'
        ],
        servings: 1,
        caloriesPerServing: 280
      }
    });
  } else {
    foodsToEat.push({
      type: 'meal',
      name: 'Veggie and Hummus Wrap',
      description: 'Fiber-rich wrap with plant protein and vegetables. Supports gut health and skin barrier.',
      recipe: {
        title: 'Veggie and Hummus Wrap',
        ingredients: [
          '1 whole wheat tortilla',
          '3 tbsp hummus',
          '1/4 cup shredded carrots',
          '1/4 cup bell peppers, sliced',
          '1/4 cup cucumber, sliced',
          '1/4 cup spinach',
          '1 tbsp sunflower seeds'
        ],
        steps: [
          'Spread hummus on tortilla.',
          'Layer vegetables on top.',
          'Sprinkle with sunflower seeds.',
          'Roll tightly and serve.'
        ],
        servings: 1,
        caloriesPerServing: 310
      }
    });
  }

  // Additional snacks for all users
  if (dietaryPreference !== 'vegetarian') {
    // Additional snack
    foodsToEat.push({
      type: 'snack',
      name: 'Trail Mix with Nuts and Dried Berries',
      description: 'Protein and healthy fats with antioxidants. Supports sustained energy and skin health.',
      recipe: {
        title: 'Trail Mix with Nuts and Dried Berries',
        ingredients: [
          '1/4 cup mixed nuts (almonds, walnuts, cashews)',
          '2 tbsp dried cranberries (unsweetened)',
          '1 tbsp pumpkin seeds',
          '1 tbsp dark chocolate chips (70%+ cacao, optional)'
        ],
        steps: [
          'Combine all ingredients in a bowl.',
          'Mix well and store in an airtight container.',
          'Enjoy as a healthy snack between meals.'
        ],
        servings: 1,
        caloriesPerServing: 250
      }
    });
  } else {
    // Additional snack for vegetarians
    foodsToEat.push({
      type: 'snack',
      name: 'Roasted Chickpeas',
      description: 'High-protein, fiber-rich snack. Supports stable blood sugar and provides plant-based protein.',
      recipe: {
        title: 'Roasted Chickpeas',
        ingredients: [
          '1 can (15 oz) chickpeas, drained and rinsed',
          '1 tbsp olive oil',
          '1/2 tsp cumin',
          '1/2 tsp paprika',
          'Salt to taste'
        ],
        steps: [
          'Preheat oven to 400°F (200°C).',
          'Pat chickpeas dry with paper towels.',
          'Toss with olive oil, cumin, paprika, and salt.',
          'Spread on baking sheet, bake for 20-25 minutes until crispy.',
          'Let cool and enjoy as a crunchy snack.'
        ],
        servings: 2,
        caloriesPerServing: 180
      }
    });
  }

  // Add antioxidant-rich snack/drink for lower scores
  if (glowScore < 70) {
    foodsToEat.push({
      type: 'snack',
      name: 'Green Tea with Lemon',
      description: 'Rich in antioxidants (EGCG) that reduce inflammation and protect against UV damage. Drink 2-3 cups daily.',
      recipe: {
        title: 'Antioxidant Green Tea',
        ingredients: [
          '1 green tea bag or 1 tsp loose green tea',
          '1 cup hot water (175-185°F)',
          '1 tsp honey (optional)',
          'Lemon slice'
        ],
        steps: [
          'Heat water to 175-185°F (not boiling).',
          'Steep green tea for 2-3 minutes.',
          'Remove tea bag or strain.',
          'Add honey and lemon if desired.',
          'Drink 2-3 cups daily for maximum benefits.'
        ],
        servings: 1,
        caloriesPerServing: 5
      }
    });
  }

  // Additional full meals for variety (all users)
  if (dietaryPreference !== 'vegetarian') {
    // Additional dinner option
    foodsToEat.push({
      type: 'meal',
      name: 'Pan-Seared White Fish with Quinoa and Asparagus',
      description: 'Lean protein with whole grains and vegetables. High in omega-3 and supports skin barrier health.',
      recipe: {
        title: 'Pan-Seared White Fish with Quinoa and Asparagus',
        ingredients: [
          '1 white fish fillet (6 oz) - cod, tilapia, or halibut',
          '1/2 cup cooked quinoa',
          '1 cup asparagus, trimmed',
          '1 tbsp olive oil',
          '1 lemon',
          '2 cloves garlic, minced',
          'Salt and pepper to taste'
        ],
        steps: [
          'Cook quinoa according to package directions.',
          'Season fish with salt, pepper, and lemon juice.',
          'Heat olive oil in a pan, sear fish for 3-4 minutes per side.',
          'Sauté asparagus with garlic until tender.',
          'Serve fish over quinoa with asparagus, drizzle with lemon.'
        ],
        servings: 1,
        caloriesPerServing: 340
      }
    });
  } else {
    // Additional dinner option for vegetarians
    foodsToEat.push({
      type: 'meal',
      name: 'Mushroom and Spinach Pasta with Whole Wheat Noodles',
      description: 'Plant-based meal with fiber and antioxidants. Supports skin health and provides sustained energy.',
      recipe: {
        title: 'Mushroom and Spinach Pasta with Whole Wheat Noodles',
        ingredients: [
          '2 oz whole wheat pasta',
          '1 cup mushrooms, sliced',
          '2 cups fresh spinach',
          '2 cloves garlic, minced',
          '1 tbsp olive oil',
          '1/4 cup vegetable broth',
          '1 tbsp nutritional yeast (optional)',
          'Salt and pepper to taste'
        ],
        steps: [
          'Cook pasta according to package directions.',
          'Heat olive oil, sauté mushrooms until golden.',
          'Add garlic and spinach, cook until wilted.',
          'Add vegetable broth and simmer.',
          'Toss with cooked pasta, add nutritional yeast if using.',
          'Season with salt and pepper, serve.'
        ],
        servings: 1,
        caloriesPerServing: 350
      }
    });
  }

  // Foods to avoid - dynamic selection based on scan results
  // Create a pool of foods to avoid and select different ones based on scan parameters
  const allFoodsToAvoid: Array<{ name: string; reason: string; priority: number }> = [
    {
      name: 'Dairy Products (Milk, Cheese, Ice Cream)',
      reason: 'Can trigger inflammation and increase insulin-like growth factor (IGF-1), which may worsen acne.',
      priority: 1
    },
    {
      name: 'High Glycemic Foods (White Bread, Pasta, Sugary Snacks)',
      reason: 'Spike blood sugar and insulin, leading to increased sebum production and inflammation.',
      priority: 1
    },
    {
      name: 'Processed Foods and Fast Food',
      reason: 'High in trans fats, refined carbs, and additives that can trigger inflammation and hormonal imbalances.',
      priority: 1
    },
    {
      name: 'Sugary Drinks and Sodas',
      reason: 'High sugar content spikes insulin and can worsen acne. Also dehydrates the skin.',
      priority: 1
    },
    {
      name: 'Excessive Dairy',
      reason: 'May trigger inflammation in some people. Consider reducing if you notice breakouts after consuming.',
      priority: 2
    },
    {
      name: 'Highly Processed Foods',
      reason: 'Low in nutrients and may contain additives that can affect skin health.',
      priority: 2
    },
    {
      name: 'Milk Chocolate and High-Sugar Chocolate',
      reason: 'High sugar and dairy content can trigger breakouts. Opt for dark chocolate (70%+ cacao) instead.',
      priority: 2
    },
    {
      name: 'Fried Foods and Trans Fats',
      reason: 'Can increase inflammation and oxidative stress, which may worsen acne and skin health.',
      priority: 2
    },
    {
      name: 'Refined Carbohydrates (White Rice, White Potatoes)',
      reason: 'High glycemic index can spike blood sugar and insulin, potentially triggering breakouts.',
      priority: 2
    },
    {
      name: 'Alcohol (Especially Sugary Cocktails)',
      reason: 'Can dehydrate the skin, increase inflammation, and disrupt hormone balance.',
      priority: 3
    },
    {
      name: 'High-Sodium Foods',
      reason: 'Can cause water retention and inflammation, potentially affecting skin appearance.',
      priority: 3
    },
    {
      name: 'Artificial Sweeteners',
      reason: 'May disrupt gut health and microbiome, which can indirectly affect skin health.',
      priority: 3
    },
    {
      name: 'Whey Protein Supplements',
      reason: 'Derived from dairy and may trigger acne in some individuals. Consider plant-based alternatives.',
      priority: 3
    },
    {
      name: 'High-Sugar Breakfast Cereals',
      reason: 'Spike blood sugar early in the day, which can affect hormone levels and skin health throughout the day.',
      priority: 3
    },
    {
      name: 'Commercially Baked Goods',
      reason: 'Often contain refined flour, sugar, and trans fats that can trigger inflammation and breakouts.',
      priority: 3
    }
  ];

  // Use a deterministic seed based on scan parameters to select foods
  // This ensures different scans get different recommendations
  const seed = Math.floor((glowScore * 100 + (estimatedPH || 5.0) * 10 + (acneSeverity === 'severe' ? 100 : acneSeverity === 'moderate' ? 50 : 0)) % 1000);
  
  // Simple seeded shuffle function
  const seededShuffle = <T,>(array: T[], seed: number): T[] => {
    const shuffled = [...array];
    let currentSeed = seed;
    for (let i = shuffled.length - 1; i > 0; i--) {
      currentSeed = (currentSeed * 9301 + 49297) % 233280;
      const j = Math.floor((currentSeed / 233280) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Select foods to avoid based on severity and seed
  const foodsToAvoid: Array<{ name: string; reason: string }> = [];
  
  if (acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    // For moderate/severe: select 4-5 high priority foods
    const highPriority = allFoodsToAvoid.filter(f => f.priority === 1);
    const shuffledHigh = seededShuffle(highPriority, seed);
    foodsToAvoid.push(...shuffledHigh.slice(0, 4).map(f => ({ name: f.name, reason: f.reason })));
    
    // Add 1-2 medium priority foods
    const mediumPriority = allFoodsToAvoid.filter(f => f.priority === 2);
    const shuffledMedium = seededShuffle(mediumPriority, seed + 100);
    foodsToAvoid.push(...shuffledMedium.slice(0, 1).map(f => ({ name: f.name, reason: f.reason })));
  } else {
    // For mild: select 2-3 medium priority foods
    const mediumPriority = allFoodsToAvoid.filter(f => f.priority === 2);
    const shuffledMedium = seededShuffle(mediumPriority, seed);
    foodsToAvoid.push(...shuffledMedium.slice(0, 3).map(f => ({ name: f.name, reason: f.reason })));
    
    // Add 1 low priority food
    const lowPriority = allFoodsToAvoid.filter(f => f.priority === 3);
    const shuffledLow = seededShuffle(lowPriority, seed + 200);
    foodsToAvoid.push(...shuffledLow.slice(0, 1).map(f => ({ name: f.name, reason: f.reason })));
  }

  // For Plus/Pro plans: Apply smarter food scoring and ranking
  let finalFoodsToEat = foodsToEat;
  
  if (subscriptionTier === 'plus' || subscriptionTier === 'pro' || subscriptionTier === 'elite') {
    // Smarter food scoring system for Plus/Pro plans
    const scoreFood = (food: DietFoodItem): number => {
      let score = 50; // Base score
      const name = food.name.toLowerCase();
      const description = food.description.toLowerCase();
      
      // Omega-3 fatty acids (anti-inflammatory, skin barrier support)
      if (name.includes('salmon') || name.includes('fish') || name.includes('omega') || description.includes('omega-3')) {
        score += 25;
      }
      if (name.includes('walnut') || name.includes('flax') || name.includes('chia')) {
        score += 15;
      }
      
      // Antioxidants (protect against free radicals)
      if (name.includes('berry') || name.includes('blueberry') || name.includes('strawberry') || description.includes('antioxidant')) {
        score += 20;
      }
      if (name.includes('green tea') || name.includes('matcha') || description.includes('green tea')) {
        score += 18;
      }
      if (name.includes('spinach') || name.includes('kale') || name.includes('broccoli') || description.includes('leafy green')) {
        score += 15;
      }
      
      // Anti-inflammatory properties
      if (name.includes('turmeric') || name.includes('ginger') || description.includes('anti-inflammatory')) {
        score += 22;
      }
      if (name.includes('quinoa') || name.includes('brown rice') || description.includes('whole grain')) {
        score += 12;
      }
      
      // Low glycemic index (stable blood sugar)
      if (name.includes('quinoa') || name.includes('brown rice') || name.includes('sweet potato') || description.includes('low glycemic')) {
        score += 15;
      }
      
      // High protein (skin repair)
      if (name.includes('chicken') || name.includes('turkey') || name.includes('egg') || name.includes('salmon') || description.includes('protein')) {
        score += 12;
      }
      
      // Zinc-rich (acne-fighting)
      if (name.includes('pumpkin seed') || name.includes('lentil') || name.includes('chickpea') || description.includes('zinc')) {
        score += 18;
      }
      
      // Vitamin C (collagen production)
      if (name.includes('citrus') || name.includes('bell pepper') || name.includes('broccoli') || description.includes('vitamin c')) {
        score += 15;
      }
      
      // Probiotics (gut-skin connection)
      if (name.includes('yogurt') || name.includes('kefir') || name.includes('fermented') || description.includes('probiotic')) {
        score += 16;
      }
      
      // Penalties for less optimal foods
      if (name.includes('white rice') || name.includes('white bread') || description.includes('refined')) {
        score -= 10;
      }
      if (name.includes('fried') || description.includes('fried')) {
        score -= 15;
      }
      if (name.includes('sugar') || description.includes('high sugar')) {
        score -= 12;
      }
      
      // Adjust based on skin health index and acne severity
      if (glowScore < 70) {
        // Lower scores need more anti-inflammatory foods
        if (name.includes('salmon') || name.includes('turmeric') || name.includes('green tea')) {
          score += 10;
        }
      }
      
      if (acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
        // Severe acne needs more zinc and anti-inflammatory foods
        if (name.includes('pumpkin seed') || name.includes('zinc') || name.includes('turmeric')) {
          score += 12;
        }
      }
      
      return score;
    };
    
    // Score all foods
    const scoredFoods = finalFoodsToEat.map(food => ({
      food,
      score: scoreFood(food)
    }));
    
    // Sort by score (highest first) for smarter recommendations
    scoredFoods.sort((a, b) => b.score - a.score);
    
    // For Pro/Elite: Use MOST ADVANCED scoring with additional factors
    if (subscriptionTier === 'pro' || subscriptionTier === 'elite') {
      // MOST ADVANCED: Re-score with additional precision factors
      scoredFoods.forEach(item => {
        let advancedScore = item.score;
        const name = item.food.name.toLowerCase();
        const description = item.food.description.toLowerCase();
        
        // MOST ADVANCED: Precision nutrient density scoring
        if (description.includes('complete protein') || description.includes('all essential amino')) {
          advancedScore += 8;
        }
        if (description.includes('fiber') && description.includes('high')) {
          advancedScore += 6;
        }
        
        // MOST ADVANCED: Bioavailability optimization
        if (name.includes('salmon') && name.includes('quinoa')) {
          advancedScore += 10; // Complete amino acid profile
        }
        if (name.includes('spinach') && name.includes('vitamin c')) {
          advancedScore += 8; // Enhanced iron absorption
        }
        
        // MOST ADVANCED: Synergistic nutrient combinations
        if ((name.includes('turmeric') && name.includes('black pepper')) || 
            (name.includes('vitamin c') && name.includes('iron'))) {
          advancedScore += 12;
        }
        
        // MOST ADVANCED: pH optimization for skin barrier
        if (estimatedPH && estimatedPH > 5.5) {
          if (name.includes('alkaline') || description.includes('ph-balancing')) {
            advancedScore += 10;
          }
        }
        
        item.score = advancedScore;
      });
      
      // Re-sort with MOST ADVANCED scores
      scoredFoods.sort((a, b) => b.score - a.score);
    }
    
    // Extract foods in smart-scored order
    finalFoodsToEat = scoredFoods.map(item => item.food);
  } else {
    // Free plan: Just shuffle for variety
    finalFoodsToEat = seededShuffleFoods(foodsToEat, foodSeed);
  }
  
  return {
    foodsToEat: finalFoodsToEat,
    foodsToAvoid,
    waterIntakeGlasses,
    ...(waterIntakeDisplay && { waterIntakeDisplay }),
    ...(waterIntakeSkinNote && { waterIntakeSkinNote }),
  };
}
